import type { ShellHydration } from './hydration.js';

export interface UploadedAttachment {
  key: string;
  url: string;
  name?: string;
  contentType?: string;
}

type AttachmentCategory = 'screenshot' | 'recording' | 'file';

export async function uploadAttachmentDataUrl(
  hydration: ShellHydration,
  dataUrl: string,
  filename: string,
  category: AttachmentCategory = 'file',
): Promise<UploadedAttachment | null> {
  try {
    const blob = await dataUrlToBlob(dataUrl);
    if (!blob) return null;

    const form = new FormData();
    form.append('file', blob, filename);
    form.append('projectId', hydration.projectId);
    form.append('category', category);
    form.append('folder', 'feedback-attachments');

    const response = await fetch(`${hydration.apiHost}/api/upload`, { method: 'POST', body: form });
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`upload ${response.status}: ${errorText || response.statusText}`);
    }

    const body = (await response.json()) as { files?: UploadedAttachment | UploadedAttachment[] };
    const entry = Array.isArray(body.files) ? body.files[0] : body.files;
    if (!entry || !entry.key) return null;

    return { key: entry.key, url: entry.url, name: filename, contentType: blob.type };
  } catch (error) {
    console.warn('[chirp-shell] attachment upload failed', (error as Error).message);
    return null;
  }
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob | null> {
  if (!dataUrl) return null;
  try {
    if (!dataUrl.startsWith('blob:') && !dataUrl.startsWith('data:')) return null;
    const response = await fetch(dataUrl);
    if (!response.ok && dataUrl.startsWith('blob:')) return null;
    const blob = await response.blob();
    const cleanMime = stripCodecParams(blob.type || extractMimeFromDataUrl(dataUrl));
    if (cleanMime && cleanMime !== blob.type) {
      return new Blob([await blob.arrayBuffer()], { type: cleanMime });
    }
    return blob;
  } catch {
    return null;
  }
}

function stripCodecParams(mime: string): string {
  if (!mime) return 'application/octet-stream';
  const base = mime.split(';')[0]?.trim();
  return base || 'application/octet-stream';
}

function extractMimeFromDataUrl(dataUrl: string): string {
  const separator = dataUrl.indexOf(';base64,');
  const fallback = dataUrl.indexOf(',');
  const headerEnd = separator >= 0 ? separator : fallback;
  if (headerEnd < 5) return 'application/octet-stream';
  return dataUrl.slice(5, headerEnd) || 'application/octet-stream';
}
